import axios from "axios";
import { getbaseUrl } from "./baserul";

let keyInfo = null;

/**
 * Loads and caches the RSA public key from GET /auth/public-key.
 * Falls back safely if disabled (503) or in unsupported environments.
 */
export const loadKey = async () => {
  if (keyInfo) return keyInfo;

  if (typeof window === "undefined" || !window?.crypto?.subtle) {
    keyInfo = { disabled: true };
    return keyInfo;
  }

  try {
    const res = await axios.get(`${getbaseUrl()}/auth/public-key`);
    const data = res?.data?.data;

    if (!data || !data.publicKey) {
      keyInfo = { disabled: true };
      return keyInfo;
    }

    const cleanKey = data.publicKey.replace(/-----[^-]+-----|\s/g, "");
    const binaryDer = atob(cleanKey);
    const der = new Uint8Array(binaryDer.length);
    for (let i = 0; i < binaryDer.length; i++) {
      der[i] = binaryDer.charCodeAt(i);
    }

    const key = await window.crypto.subtle.importKey(
      "spki",
      der,
      { name: "RSA-OAEP", hash: "SHA-256" },
      false,
      ["encrypt"]
    );

    const serverTime = Number(data.serverTime) || Date.now();
    keyInfo = {
      kid: data.kid,
      prefix: data.fieldPrefix || "enc:v1:",
      key,
      clockOffset: serverTime - Date.now(),
      required: Boolean(data.required),
    };
  } catch (err) {
    if (err?.response?.status === 503 || !err?.response?.data?.data?.required) {
      keyInfo = { disabled: true };
    } else {
      keyInfo = null;
      throw err;
    }
  }

  return keyInfo;
};

export const loadPublicKey = loadKey;

/**
 * Encrypts a password field per specification:
 * JSON payload {"v": "<password>", "ts": <serverTime>}
 * Encrypted with RSA-OAEP SHA-256 -> enc:v1:<kid>:<base64>
 */
export const encryptField = async (value) => {
  if (!value || typeof value !== "string") return value;
  if (value.startsWith("enc:v1:")) return value;

  try {
    const info = await loadKey();
    if (!info || info.disabled || !info.key) return value;

    const payload = JSON.stringify({
      v: value,
      ts: Date.now() + (info.clockOffset || 0),
    });

    const bytes = new TextEncoder().encode(payload);
    const ct = await window.crypto.subtle.encrypt(
      { name: "RSA-OAEP" },
      info.key,
      bytes
    );

    const binary = String.fromCharCode(...new Uint8Array(ct));
    return `${info.prefix}${info.kid}:${btoa(binary)}`;
  } catch (err) {
    console.error("Encryption error:", err);
    if (keyInfo?.required) throw err;
    return value;
  }
};

/**
 * Clears the cached encryption key.
 */
export const resetEncryptionKey = () => {
  keyInfo = null;
};

export default {
  loadKey,
  loadPublicKey,
  encryptField,
  resetEncryptionKey,
};
